<template>
  <Container v-bind="containerProps">
    <draggable
      v-model="_value"
      draggable=".item"
      :item-key="(item: string) => item"
      class="h-full flex items-center flex-wrap"
      @keydown.escape="handleClear()"
    >
      <template #item="{ element: item, index }">
        <div class="item py-1 pl-3 rounded-md bg-blue-100 dark:bg-blue-800/30 text-blue-600 dark:text-blue-500 text-sm mr-1 my-px flex items-center transition cursor-move">
          {{ item }}

          <o-icon icon="x" :size="12" class="bg-blue-200 dark:bg-blue-800/30 rounded-md h-5 w-5 flex items-center justify-center ml-3 mr-2 hover:bg-blue-300 dark:hover:bg-blue-700/50 transition cursor-pointer" @click="handleRemove(index)" />
        </div>
      </template>

      <template #footer>
        <div
          class="btn-group list-group-item grow"
          role="group"
          :aria-label="$t('components.fields.list.basicExample')"
        >
          <input
            ref="input"
            v-model="inputValue"
            type="text"
            :placeholder="$t('components.fields.list.placeholder')"
            class="w-full h-8 px-2 bg-transparent text-sm mt-px"
            @blur="handleBlur"
            @keydown.enter="handleAdd"
            @keydown.backspace="handleBackspace"
          >
        </div>
      </template>
    </draggable>
  </Container>
</template>

<script setup lang="ts">
import draggable from 'vuedraggable'

import Container from './Container.vue'

interface Props {
  modelValue: any[]
  baseClass?: string | Record<string, any> | string[]
  defaultSizeClass?: string
  // input mixin props
  id?: string | number | null
  name?: string | null
  required?: boolean
  readonly?: boolean
  clear?: boolean
  disabled?: boolean
  size?: string | number | null
  pattern?: string | null
  placeholder?: string | number | null
  leading?: string | string[] | null
  trailing?: string | string[] | null
  loading?: boolean
  border?: string | Record<string, any> | string[] | boolean
  rounded?: string | Record<string, any> | string[] | boolean
  shadow?: string | Record<string, any> | string[] | boolean
  highlight?: string | Record<string, any> | string[] | boolean
  defaultStatusClass?: string | Record<string, any> | string[]
  warningStatusClass?: string | Record<string, any> | string[]
  errorStatusClass?: string | Record<string, any> | string[]
  successStatusClass?: string | Record<string, any> | string[]
  disabledClass?: string | Record<string, any> | string[]
  largeSizeClass?: string | Record<string, any> | string[]
  smallSizeClass?: string | Record<string, any> | string[]
  status?: boolean | string | null
}

const props = withDefaults(defineProps<Props>(), {
  baseClass: 'h-auto w-full relative flex items-center',
  defaultSizeClass: 'p-2px',
  id: null,
  name: null,
  required: false,
  readonly: false,
  clear: false,
  disabled: false,
  size: null,
  pattern: null,
  placeholder: null,
  leading: null,
  trailing: null,
  loading: false,
  border: 'border dark:border-gray-800',
  rounded: 'rounded-md',
  shadow: 'shadow-sm',
  highlight: 'focus-within:ring-2',
  defaultStatusClass: 'bg-white dark:bg-gray-900',
  warningStatusClass: 'border-yellow-200 bg-yellow-50 dark:bg-yellow-400/20',
  errorStatusClass: 'border-red-300 bg-red-100 dark:bg-red-400/20',
  successStatusClass: 'border-green-200 bg-green-100 dark:bg-green-400/20',
  disabledClass: 'bg-gray-50 dark:bg-gray-800 cursor-not-allowed opacity-75 dark:opacity-50',
  largeSizeClass: 'px-4 text-lg h-12',
  smallSizeClass: 'px-2 text-sm h-8',
  status: null,
})

const emit = defineEmits<{
  (e: 'update:modelValue', value: any[]): void
  (e: 'blur', value: any): void
  (e: 'change', value: any[]): void
}>()

const input = ref<any>()
const inputValue = ref('')

const _value = computed({
  get () {
    return props.modelValue
  },
  set (value: any[]) {
    emit('update:modelValue', value)
  },
})

// -- classes mixin --
const noStatus = computed(() => props.status === null)
const isError = computed(() => props.status === false || props.status === 'error')
const isSuccess = computed(() => props.status === true || props.status === 'success')
const isWarning = computed(() => props.status === 'warning')

const statusClasses = computed(() => {
  const classes: any[] = []
  if (props.disabled) {
    if (props.disabledClass) classes.push(props.disabledClass)
  }
  if (props.size === null && props.defaultSizeClass) classes.push(props.defaultSizeClass)
  else if (props.size === 'sm' && props.smallSizeClass) classes.push(props.smallSizeClass)
  else if (props.size === 'lg' && props.largeSizeClass) classes.push(props.largeSizeClass)
  if (!props.disabled && noStatus.value && props.defaultStatusClass) classes.push(props.defaultStatusClass)
  if (isError.value && props.errorStatusClass) classes.push(props.errorStatusClass)
  else if (isSuccess.value && props.successStatusClass) classes.push(props.successStatusClass)
  else if (isWarning.value && props.warningStatusClass) classes.push(props.warningStatusClass)
  return classes
})

const currentClass = computed(() => {
  const classes: any[] = [props.baseClass]
  return classes.concat(statusClasses.value)
})

const containerProps = computed(() => {
  const classes = [...currentClass.value]
  if (props.border) classes.push(props.border as string)
  if (props.rounded) classes.push(props.rounded as string)
  if (props.shadow) classes.push(props.shadow as string)
  if (props.highlight) classes.push(props.highlight as string)
  return {
    class: classes,
    baseClass: props.baseClass,
    leading: props.leading,
    trailing: props.trailing,
    loading: props.loading,
  }
})

function handleAdd () {
  const val = inputValue.value

  if (val) {
    const splitInput = val.split(',')
    if (splitInput.length > 1) {
      const trimmedSplit = splitInput.map(i => i.trim())
      const filtered = trimmedSplit.filter(i => i)
      _value.value = [..._value.value, ...filtered]
    } else {
      _value.value = [..._value.value, val]
    }

    inputValue.value = ''
    nextTick(() => input.value?.$el?.focus())
    emit('change', _value.value)
  }
}

function handleClear () {
  inputValue.value = ''
}

function handleRemove (index: number) {
  _value.value = _value.value.filter((_: any, i: number) => i !== index)

  emit('blur', _value.value)
  emit('change', _value.value)
}

function handleBlur () {
  handleAdd()
  emit('blur', _value.value)
}

function handleBackspace () {
  if (!inputValue.value) {
    _value.value = _value.value.slice(0, -1)
  }
}
</script>

<style scoped>
.flip-list-move {
  transition: transform 0.5s;
}
.no-move {
  transition: transform 0s;
}

.sortable-ghost {
  opacity: 0.4;
  border: 1px dashed gray
}
</style>
